import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAnimatedNumber } from '@/pages/home/components/calculators/AnimatedNumber';
import { PRODUCTS, CURRENCIES, RISK_QUICK, SL_QUICK } from '@/mocks/lot';
import { formatVN, Label, NumberInput, SelectInput, QuickChips, Toggle } from './ui';
import type { LotCalculatorState, PipMode } from '../useLotCalculator';

export default function LotCalculator({ calc }: { calc: LotCalculatorState }) {
  const {
    balance,
    setBalance,
    risk,
    setRisk,
    pair,
    setPair,
    currency,
    setCurrency,
    stopLoss,
    setStopLoss,
    pipMode,
    setPipMode,
    manualPip,
    setManualPip,
    product,
    curr,
    pipValue,
    riskAmount,
    lotSize,
    balanceInvalid,
    slInvalid,
    pipInvalid,
    hasError,
    riskPosition,
    riskLevel,
    openExplanation,
  } = calc;

  const animatedLot = useAnimatedNumber(lotSize, 500);
  const animatedRiskAmount = useAnimatedNumber(riskAmount, 500);

  // Flash feedback when clicking the calculate button
  const [flash, setFlash] = useState(false);
  const triggerFlash = () => {
    setFlash(true);
    window.setTimeout(() => setFlash(false), 700);
  };

  const handleCalculate = () => {
    triggerFlash();
    openExplanation();
  };

  // Highlight the pip value line when the product changes
  const [pipHighlight, setPipHighlight] = useState(false);
  const prevPairRef = useRef(pair);
  useEffect(() => {
    if (prevPairRef.current !== pair) {
      prevPairRef.current = pair;
      setPipHighlight(true);
      const t = window.setTimeout(() => setPipHighlight(false), 900);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [pair]);

  // Gauge helpers
  const gaugeDeg = Math.min(risk / 5, 1) * 360;
  const gaugeColor = risk < 1 ? '#34d399' : risk <= 2 ? '#fbbf24' : '#fb7185';

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
      {/* Left: inputs */}
      <div className="lg:col-span-3">
        <div className="h-full rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                <i className="ri-settings-3-line text-lg" />
              </span>
              <div>
                <h3 className="font-display text-lg font-bold leading-tight text-slate-900">Thông tin giao dịch</h3>
                <p className="text-xs text-slate-400">Nhập thông số, kết quả cập nhật tức thì</p>
              </div>
            </div>
            <span className="hidden items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600 sm:flex">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
              </span>
              Trực tiếp
            </span>
          </div>

          <div className="space-y-5">
            <div>
              <Label>Số dư tài khoản</Label>
              <NumberInput value={balance} onChange={setBalance} suffix={curr.label} min={0} step={100} />
              {balanceInvalid && <ErrorHint>Số dư tài khoản phải lớn hơn 0.</ErrorHint>}
            </div>

            <div>
              <Label hint={`${formatVN(risk, 2, 2)} %`}>Mức rủi ro</Label>
              <NumberInput value={risk} onChange={setRisk} suffix="%" min={0} step={0.5} />
              <div className="mt-2.5">
                <QuickChips
                  options={RISK_QUICK}
                  value={risk}
                  onChange={setRisk}
                  format={(v) => `${formatVN(v, 1, 1)}%`}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <Label>Sản phẩm giao dịch</Label>
                <SelectInput
                  value={pair}
                  onChange={setPair}
                  options={PRODUCTS.map((p) => ({ value: p.value, label: p.label }))}
                />
              </div>
              <div>
                <Label>Tiền tệ tài khoản</Label>
                <SelectInput
                  value={currency}
                  onChange={setCurrency}
                  options={CURRENCIES.map((c) => ({ value: c.value, label: c.label }))}
                />
              </div>
            </div>

            <div>
              <Label>Stop Loss</Label>
              <NumberInput value={stopLoss} onChange={setStopLoss} suffix="Pips" min={0} step={5} />
              <div className="mt-2.5">
                <QuickChips options={SL_QUICK} value={stopLoss} onChange={setStopLoss} />
              </div>
              {slInvalid && <ErrorHint>Khoảng cách Stop Loss phải lớn hơn 0.</ErrorHint>}
            </div>

            <div>
              <Label>Giá trị Pip</Label>
              <Toggle
                options={[
                  { value: 'auto', label: 'Tự động tính' },
                  { value: 'manual', label: 'Nhập thủ công' },
                ]}
                value={pipMode}
                onChange={(v) => setPipMode(v as PipMode)}
              />
              {pipMode === 'auto' ? (
                <p
                  className={`mt-2.5 flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs transition-colors duration-300 ${
                    pipHighlight ? 'bg-violet-100 font-semibold text-violet-700' : 'text-slate-400'
                  }`}
                >
                  <i className="ri-flashlight-line" />
                  Giá trị Pip tự động: {formatVN(product.pipValue * curr.rate, 0, 2)} {curr.label} / lot · {product.label}
                </p>
              ) : (
                <div className="mt-2.5">
                  <NumberInput value={manualPip} onChange={setManualPip} suffix={`${curr.label} / lot`} min={0} step={0.1} />
                  {pipInvalid && <ErrorHint>Giá trị Pip phải lớn hơn 0.</ErrorHint>}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handleCalculate}
              className="group flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-violet-600 px-6 py-3.5 text-base font-semibold text-white transition-all hover:bg-violet-700 active:scale-[0.99]"
            >
              <i className="ri-calculator-line text-lg" />
              Tính khối lượng
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Right: result */}
      <div className="lg:col-span-2">
        <div className={`relative flex h-full min-h-[520px] flex-col overflow-hidden rounded-3xl bg-gradient-to-br from-violet-600 via-violet-700 to-indigo-800 p-6 text-white transition-all duration-300 md:p-7 ${flash ? 'ring-4 ring-amber-300/80' : 'ring-0 ring-transparent'}`}>
          {/* decorative glows */}
          <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-amber-400/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-indigo-400/25 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:28px_28px]" />

          <div className="relative flex flex-1 flex-col">
            {/* header */}
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-violet-100">Khối lượng đề xuất</p>
              <span className="flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-violet-100 backdrop-blur">
                <i className="ri-flashlight-line" />
                {product.label}
              </span>
            </div>

            {hasError ? (
              <div className="flex flex-1 flex-col items-center justify-center py-10 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 text-amber-300 backdrop-blur">
                  <i className="ri-alert-line text-3xl" />
                </span>
                <p className="mt-4 text-sm font-semibold text-white">Vui lòng nhập thông tin hợp lệ</p>
                <p className="mt-1 text-xs text-violet-200">Kiểm tra lại số dư, Stop Loss hoặc giá trị Pip.</p>
              </div>
            ) : (
              <>
                {/* big number */}
                <div className="mt-6 flex flex-col items-center text-center">
                  <div className="flex items-end gap-2.5">
                    <span
                      className="font-mono text-6xl font-bold leading-none tabular-nums text-amber-300 md:text-7xl"
                      style={{ textShadow: '0 0 34px rgba(251,191,36,0.45)' }}
                    >
                      {formatVN(animatedLot, 2, 2)}
                    </span>
                    <span className="mb-1 font-display text-2xl font-bold text-white/90">Lot</span>
                  </div>
                  <p className="mt-2.5 text-sm text-violet-200">Khối lượng giao dịch đề xuất</p>
                </div>

                {/* gauge */}
                <div className="mt-7 flex flex-col items-center">
                  <div
                    className="relative flex h-32 w-32 items-center justify-center rounded-full"
                    style={{
                      background: `conic-gradient(${gaugeColor} 0deg, ${gaugeColor} ${gaugeDeg}deg, rgba(255,255,255,0.12) ${gaugeDeg}deg 360deg)`,
                    }}
                  >
                    <div className="flex h-[104px] w-[104px] flex-col items-center justify-center rounded-full bg-violet-700/90 backdrop-blur">
                      <span className="text-[10px] font-medium uppercase tracking-wide text-violet-200">Rủi ro</span>
                      <span className="font-mono text-xl font-bold tabular-nums text-white">{formatVN(risk, 2, 2)}%</span>
                    </div>
                  </div>
                  <span
                    className="mt-3 rounded-full px-3 py-1 text-xs font-semibold"
                    style={{ backgroundColor: `${gaugeColor}26`, color: gaugeColor }}
                  >
                    {riskLevel}
                  </span>
                </div>
              </>
            )}

            {/* stats */}
            <div className="mt-7 grid grid-cols-2 gap-2.5">
              <GlassStat label="Rủi ro tối đa" value={`${formatVN(animatedRiskAmount, 0, 2)} ${curr.label}`} />
              <GlassStat label="Mức rủi ro" value={`${formatVN(risk, 2, 2)} %`} />
              <GlassStat label="Stop Loss" value={`${formatVN(stopLoss, 0, 0)} Pips`} />
              <GlassStat label="Giá trị mỗi Pip" value={`${formatVN(pipValue, 0, 2)} ${curr.label}`} />
            </div>

            {/* formula breakdown */}
            <div className="mt-auto pt-6">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                <p className="text-[11px] font-medium uppercase tracking-wide text-violet-200">Công thức</p>
                <p className="mt-1.5 font-mono text-sm font-semibold tabular-nums text-white">
                  {formatVN(riskAmount, 0, 2)} ÷ ({formatVN(stopLoss, 0, 0)} × {formatVN(pipValue, 0, 2)}) ={' '}
                  <span className="text-amber-300">{formatVN(lotSize, 2, 2)}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GlassStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/10 p-3.5 backdrop-blur">
      <p className="text-[11px] font-medium text-violet-200">{label}</p>
      <p className="mt-1 font-mono text-sm font-bold tabular-nums text-white">{value}</p>
    </div>
  );
}

function ErrorHint({ children }: { children: ReactNode }) {
  return (
    <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-500">
      <i className="ri-error-warning-line" />
      {children}
    </p>
  );
}